
import React, { useState } from 'react';
import { Search, ChevronDown, Star, Clock, MapPin } from 'lucide-react';

// Mock station data
const stations = [
  {
    id: 'BKK001',
    name: 'Sukhumvit 71',
    area: 'Bangkok - East',
    format: 'Urban Premium',
    volume: 12500,
    margin: 0.89,
    competitors: 3,
    lat: 13.7307,
    lng: 100.5847,
  },
  {
    id: 'BKK042',
    name: 'Rama IX',
    area: 'Bangkok - Central',
    format: 'Urban Premium',
    volume: 14200,
    margin: 0.92,
    competitors: 4,
    lat: 13.7563,
    lng: 100.5669,
  },
  {
    id: 'CNX015',
    name: 'Chiang Mai Highway',
    area: 'Northern',
    format: 'Highway Transit',
    volume: 18700,
    margin: 0.81,
    competitors: 2,
    lat: 18.7883,
    lng: 98.9853,
  },
  {
    id: 'HY021',
    name: 'Pattaya Approach',
    area: 'Eastern',
    format: 'Highway Transit',
    volume: 16900,
    margin: 0.78,
    competitors: 3,
    lat: 12.9236,
    lng: 100.8825,
  },
  {
    id: 'UDN007',
    name: 'Udon Central',
    area: 'Northeastern',
    format: 'Rural Standard',
    volume: 9800,
    margin: 0.85,
    competitors: 2,
    lat: 17.4157,
    lng: 102.7859,
  },
];

// Recent and favorite stations for quick access
const recentStations = ['BKK001', 'CNX015'];
const favoriteStations = ['BKK042', 'HY021'];

const StationSelector = ({ onSelectStation, selectedStation }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [regionFilter, setRegionFilter] = useState('All Regions');
  const [formatFilter, setFormatFilter] = useState('All Formats');
  const [quickAccessTab, setQuickAccessTab] = useState('all');

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredStations = stations.filter(station => {
    const matchesSearch = station.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                        station.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = regionFilter === 'All Regions' || station.area.includes(regionFilter.replace('All Regions', ''));
    const matchesFormat = formatFilter === 'All Formats' || station.format === formatFilter;
    
    if (quickAccessTab === 'recent') {
      return matchesSearch && matchesRegion && matchesFormat && recentStations.includes(station.id);
    } else if (quickAccessTab === 'favorites') {
      return matchesSearch && matchesRegion && matchesFormat && favoriteStations.includes(station.id);
    }
    
    return matchesSearch && matchesRegion && matchesFormat;
  });

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Select Station for Analysis</h2>
        
        {/* Quick Access Tabs */}
        <div className="flex mb-4 border-b border-gray-200">
          <button 
            className={`px-4 py-2 font-medium text-sm ${quickAccessTab === 'all' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'}`}
            onClick={() => setQuickAccessTab('all')}
          >
            All Stations
          </button>
          <button 
            className={`px-4 py-2 font-medium text-sm flex items-center ${quickAccessTab === 'recent' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'}`}
            onClick={() => setQuickAccessTab('recent')}
          >
            <Clock size={16} className="mr-1" /> Recent
          </button>
          <button 
            className={`px-4 py-2 font-medium text-sm flex items-center ${quickAccessTab === 'favorites' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'}`}
            onClick={() => setQuickAccessTab('favorites')}
          >
            <Star size={16} className="mr-1" /> Favorites
          </button>
        </div>
        
        <div className="flex space-x-4 mb-6">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={18} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search stations by name or ID..."
              className="block w-full bg-gray-50 border border-gray-300 rounded-md py-2 pl-10 pr-3 text-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="w-48">
            <div className="relative">
              <select 
                className="appearance-none block w-full bg-gray-50 border border-gray-300 rounded-md py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
              >
                <option>All Regions</option>
                <option>Bangkok</option>
                <option>Central</option>
                <option>Northern</option>
                <option>Northeastern</option>
                <option>Southern</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <ChevronDown size={16} />
              </div>
            </div>
          </div>
          <div className="w-48">
            <div className="relative">
              <select 
                className="appearance-none block w-full bg-gray-50 border border-gray-300 rounded-md py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                value={formatFilter}
                onChange={(e) => setFormatFilter(e.target.value)}
              >
                <option>All Formats</option>
                <option>Urban Premium</option>
                <option>Highway Transit</option>
                <option>Rural Standard</option>
                <option>Suburban Mixed</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <ChevronDown size={16} />
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Station ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Station Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Area
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Format
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Volume (L/day)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Margin (฿/L)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Competitors
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStations.map((station) => (
                <tr
                  key={station.id}
                  className={`hover:bg-gray-50 cursor-pointer ${
                    selectedStation && selectedStation.id === station.id ? 'bg-purple-50' : ''
                  }`}
                  onClick={() => onSelectStation(station)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div className="flex items-center">
                      {favoriteStations.includes(station.id) && (
                        <Star size={16} className="text-yellow-400 mr-1 inline" />
                      )}
                      {station.id}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex items-center">
                    <MapPin size={16} className="text-purple-500 mr-1" />
                    {station.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {station.area}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {station.format}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {station.volume.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {station.margin.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {station.competitors}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      className="text-purple-600 hover:text-purple-900"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectStation(station);
                      }}
                    >
                      Analyze
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StationSelector;
